import {
  findNodeAtOffset,
  modify,
  parseTree,
  type Node,
  type JSONPath,
} from "jsonc-parser"
import type { editor } from "monaco-editor"
import type { JsonSchema } from "@/v2-backoffice-client/feature/configuration/feature.configuration.types"

/**
 * Pure logic behind the inspector lens: resolve the JSON node under the
 * editor cursor, describe its properties (with schema hints), and apply
 * structured edits back onto the text via minimal AST-preserving patches so
 * the operator's formatting survives.
 */

const JSON_FORMATTING = { insertSpaces: true, tabSize: 2 }

type InspectorEntryKind =
  | "boolean"
  | "enum"
  | "number"
  | "color"
  | "string"
  | "object"
  | "array"
  | "null"

export type InspectorEntry = {
  key: string
  path: JSONPath
  kind: InspectorEntryKind
  value: unknown
  /** Offset of the property value in the document (for cursor jumps). */
  offset: number
  enumValues?: string[]
  description?: string
  minimum?: number
  maximum?: number
}

type InspectorState = {
  /** Path of the object the cursor is inside (empty = document root). */
  path: JSONPath
  entries: InspectorEntry[]
}

function getNodePath(node: Node): JSONPath {
  const path: JSONPath = []
  let current: Node | undefined = node
  while (current?.parent) {
    const parent: Node = current.parent
    if (parent.type === "property") {
      // current is the value; segment name is the property's first child
      const keyNode = parent.children?.[0]
      if (keyNode && keyNode !== current) path.unshift(String(keyNode.value))
    } else if (parent.type === "array") {
      path.unshift(parent.children?.indexOf(current) ?? 0)
    }
    current = parent
  }
  return path
}

/** Walks our curated schema along a JSON path (properties/items/anyOf). */
function getSchemaAtPath(
  schema: JsonSchema | undefined,
  path: JSONPath,
): JsonSchema | undefined {
  let current = schema
  for (const segment of path) {
    if (!current) return undefined
    if (typeof segment === "number") {
      const items = current.items as JsonSchema | undefined
      current = items
      continue
    }
    const properties = current.properties as
      | Record<string, JsonSchema>
      | undefined
    let next = properties?.[segment]
    if (!next && Array.isArray(current.anyOf)) {
      for (const candidate of current.anyOf as JsonSchema[]) {
        const candidateProperties = candidate.properties as
          | Record<string, JsonSchema>
          | undefined
        next = candidateProperties?.[segment]
        if (next) break
      }
    }
    current = next
  }
  return current
}

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/

function getEntryKind(
  valueNode: Node,
  schema: JsonSchema | undefined,
): InspectorEntryKind {
  if (valueNode.type === "object") return "object"
  if (valueNode.type === "array") return "array"
  if (valueNode.type === "null") return "null"
  if (valueNode.type === "boolean") return "boolean"
  if (valueNode.type === "number") return "number"
  if (Array.isArray(schema?.enum)) return "enum"
  if (
    typeof valueNode.value === "string" &&
    HEX_COLOR_PATTERN.test(valueNode.value)
  ) {
    return "color"
  }
  return "string"
}

/**
 * Resolves the inspector view for a cursor offset: the nearest enclosing
 * object node and one entry per property. Returns null when the document
 * doesn't parse or the root isn't an object/array.
 */
export function getInspectorState(
  text: string,
  offset: number,
): InspectorState | null {
  const root = parseTree(text)
  if (!root) return null

  let node = findNodeAtOffset(root, offset) ?? root

  // Climb to the nearest object: a primitive/property resolves to its owner,
  // an array resolves to its parent object so the panel stays useful.
  while (node && node.type !== "object") {
    if (!node.parent) break
    node = node.parent
  }
  if (!node || node.type !== "object") {
    node = root.type === "object" ? root : (root.children?.[0] ?? root)
  }
  if (node.type !== "object") return null

  const path = getNodePath(node)

  const entries: InspectorEntry[] = []
  for (const property of node.children ?? []) {
    const keyNode = property.children?.[0]
    const valueNode = property.children?.[1]
    if (!keyNode || !valueNode) continue

    const key = String(keyNode.value)
    entries.push({
      key,
      path: [...path, key],
      value: getNodeValue(valueNode),
      offset: valueNode.offset,
      // Schema hints (enum upgrade, docs, bounds) are added later by
      // describeEntries, keeping this function parse-only.
      kind: getEntryKind(valueNode, undefined),
    })
  }

  return { path, entries }
}

function getNodeValue(node: Node): unknown {
  if (node.type === "object" || node.type === "array") return undefined
  return node.value
}

/** Enriches parse-derived entries with schema hints (enum, docs, bounds). */
export function describeEntries(
  state: InspectorState,
  rootSchema: JsonSchema | undefined,
): InspectorEntry[] {
  return state.entries.map((entry) => {
    const schema = getSchemaAtPath(rootSchema, entry.path)
    const enumValues = Array.isArray(schema?.enum)
      ? (schema.enum as string[])
      : undefined

    let kind = entry.kind
    if (enumValues && (kind === "string" || kind === "number")) kind = "enum"

    return {
      ...entry,
      kind,
      enumValues,
      description:
        typeof schema?.description === "string"
          ? schema.description
          : typeof schema?.markdownDescription === "string"
            ? schema.markdownDescription
            : undefined,
      minimum: typeof schema?.minimum === "number" ? schema.minimum : undefined,
      maximum: typeof schema?.maximum === "number" ? schema.maximum : undefined,
    }
  })
}

/**
 * Applies a single value change at a JSON path onto the Monaco editor via
 * `executeEdits`, so undo/redo keeps working and only the touched range is
 * rewritten (formatting elsewhere is preserved). `value: undefined` removes
 * the node; `isArrayInsertion` inserts at the path's index instead of
 * replacing it.
 */
export function applyInspectorEdit(
  editorInstance: editor.IStandaloneCodeEditor,
  path: JSONPath,
  value: unknown,
  options?: { isArrayInsertion?: boolean },
) {
  const model = editorInstance.getModel()
  if (!model) return

  const text = model.getValue()
  const edits = modify(text, path, value, {
    formattingOptions: JSON_FORMATTING,
    isArrayInsertion: options?.isArrayInsertion,
  })

  editorInstance.executeEdits(
    "layer-inspector",
    edits.map((edit) => ({
      range: monacoRangeFromEdit(model, edit.offset, edit.length),
      text: edit.content,
      forceMoveMarkers: true,
    })),
  )
}

function monacoRangeFromEdit(
  model: editor.ITextModel,
  offset: number,
  length: number,
) {
  const start = model.getPositionAt(offset)
  const end = model.getPositionAt(offset + length)
  return {
    startLineNumber: start.lineNumber,
    startColumn: start.column,
    endLineNumber: end.lineNumber,
    endColumn: end.column,
  }
}
